import React, { useCallback, useEffect, useState, useRef } from 'react';
import { MicIcon, SquareIcon, XIcon, PlusIcon, CheckIcon, PencilIcon, SparklesIcon, LoaderIcon, AlertTriangleIcon, ShieldAlertIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps, StepHazardData, StepHazardItem, GeneralHazardItem, ECMDriver } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { EditItemModal } from '../ui/EditItemModal';
import OpenAI from 'openai';
import { getHazardVoiceExtractionPrompt } from '../../config/aiPrompts';
import { useI18n } from '../../config/i18n';
// ── OpenAI helpers ───────────────────────────────────────────────────────────
const STORAGE_KEY = 'ptp_openai_api_key';
function getSavedApiKey(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}
function getOpenAIClient(): OpenAI {
  const apiKey = getSavedApiKey();
  return new OpenAI({
    apiKey: apiKey || 'missing-key',
    dangerouslyAllowBrowser: true
  });
}
// ── Types ────────────────────────────────────────────────────────────────────
interface ExtractedHazard {
  hazard: string;
  source: string;
  ecmDriver: ECMDriver;
  targetStep: number | 'general';
  selected: boolean;
}
interface HazardReviewScreenProps extends ScreenProps {
  stepHazardData: StepHazardData[];
  generalHazards: GeneralHazardItem[];
  onUpdateStepHazardData: (data: StepHazardData[]) => void;
  onUpdateGeneralHazards: (hazards: GeneralHazardItem[]) => void;
}
function getEcmColorClasses(category: ECMDriver) {
  switch (category) {
    case 'E':
      return 'bg-emerald-50 text-emerald-600 border-emerald-200';
    case 'C':
      return 'bg-blue-50 text-blue-600 border-blue-200';
    case 'M':
      return 'bg-amber-50 text-amber-600 border-amber-200';
  }
}
function getEcmLabel(driver: ECMDriver) {
  switch (driver) {
    case 'E':
      return 'Environment';
    case 'C':
      return 'Capability';
    case 'M':
      return 'Motivation';
  }
}
export function HazardReviewScreen({
  data,
  stepHazardData,
  generalHazards,
  onUpdateStepHazardData,
  onUpdateGeneralHazards,
  onNext
}: HazardReviewScreenProps) {
  const {
    lang,
    t
  } = useI18n();
  // ── Manual Add State ────────────────────────────────────────────────────────
  const [showAddInput, setShowAddInput] = useState(false);
  const [newHazardText, setNewHazardText] = useState('');
  const [addTargetStep, setAddTargetStep] = useState<number | 'general'>(0);
  // ── Voice Modal State ───────────────────────────────────────────────────────
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [extractedHazards, setExtractedHazards] = useState<ExtractedHazard[]>([]);
  // MediaRecorder refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  // ── Edit State ──────────────────────────────────────────────────────────────
  const [editingHazard, setEditingHazard] = useState<{
    id: string;
    text: string;
    isGeneral: boolean;
  } | null>(null);
  // ── Computed ────────────────────────────────────────────────────────────────
  // "relevant" on hazards starts as `true` from AI generation. We treat `true` as
  // "reviewed & accepted" and `false` as "reviewed & rejected". To track unreviewed
  // items we use a local set of IDs that have been explicitly touched.
  // Actually, looking at the existing data model, `relevant` is a boolean (not nullable).
  // We need a way to track which hazards have been explicitly reviewed.
  // We'll use a local Set of reviewed hazard IDs.
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(() => new Set());
  const allStepHazards = stepHazardData.flatMap(s => s.hazards);
  const allHazards = [...allStepHazards, ...generalHazards];
  const totalCount = allHazards.length;
  const reviewedCount = allHazards.filter(h => reviewedIds.has(h.id)).length;
  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSetRelevant = (hazardId: string, value: boolean, isGeneral: boolean) => {
    setReviewedIds(prev => new Set(prev).add(hazardId));
    if (isGeneral) {
      const updated = generalHazards.map(h => h.id === hazardId ? {
        ...h,
        relevant: value
      } : h);
      onUpdateGeneralHazards(updated);
    } else {
      const updated = stepHazardData.map(step => ({
        ...step,
        hazards: step.hazards.map(h => h.id === hazardId ? {
          ...h,
          relevant: value
        } : h)
      }));
      onUpdateStepHazardData(updated);
    }
  };
  const handleAddHazard = () => {
    const trimmed = newHazardText.trim();
    if (!trimmed) return;
    const newId = `h-manual-${Date.now()}`;
    setReviewedIds(prev => new Set(prev).add(newId));
    if (addTargetStep === 'general') {
      const newHazard: GeneralHazardItem = {
        id: newId,
        hazard: trimmed,
        source: 'Manually identified',
        ecmDriver: 'E',
        controls: {
          eliminateSubstitute: 'Assess and determine',
          engineering: 'Apply engineering controls',
          adminTraining: 'Follow procedures',
          ppe: 'Standard PPE'
        },
        equipment: [],
        relevant: true
      };
      onUpdateGeneralHazards([...generalHazards, newHazard]);
    } else {
      const stepIdx = addTargetStep as number;
      if (stepIdx >= 0 && stepIdx < stepHazardData.length) {
        const newHazard: StepHazardItem = {
          id: newId,
          hazard: trimmed,
          source: 'Manually identified',
          ecmDriver: 'E',
          controls: {
            eliminateSubstitute: 'Assess and determine',
            engineering: 'Apply engineering controls',
            adminTraining: 'Follow procedures',
            ppe: 'Standard PPE'
          },
          equipment: [],
          relevant: true
        };
        const updated = stepHazardData.map((step, i) => i === stepIdx ? {
          ...step,
          hazards: [...step.hazards, newHazard]
        } : step);
        onUpdateStepHazardData(updated);
      }
    }
    setNewHazardText('');
    setShowAddInput(false);
  };
  // ── Edit Hazard Handler ──────────────────────────────────────────────────────
  const handleEditHazard = (id: string, newText: string, isGeneral: boolean) => {
    if (isGeneral) {
      const updated = generalHazards.map(h => h.id === id ? {
        ...h,
        hazard: newText
      } : h);
      onUpdateGeneralHazards(updated);
    } else {
      const updated = stepHazardData.map(step => ({
        ...step,
        hazards: step.hazards.map(h => h.id === id ? {
          ...h,
          hazard: newText
        } : h)
      }));
      onUpdateStepHazardData(updated);
    }
    setEditingHazard(null);
  };
  // ── Voice Recording ────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.start(250);
      setIsRecording(true);
      setVoiceTranscription('');
      setHasRecorded(false);
      setExtractedHazards([]);
    } catch (err) {
      console.error('Microphone access denied:', err);
      setVoiceError('Microphone access denied. Please allow permission.');
    }
  }, []);
  const stopRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      setIsRecording(false);
      return;
    }
    setIsRecording(false);
    setIsTranscribing(true);
    setVoiceError(null);
    const audioBlob = await new Promise<Blob>(resolve => {
      mediaRecorder.onstop = () => {
        resolve(new Blob(chunksRef.current, {
          type: 'audio/webm'
        }));
      };
      mediaRecorder.stop();
    });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioBlob.size < 1000) {
      setIsTranscribing(false);
      setVoiceError('Recording too short. Please speak longer.');
      return;
    }
    try {
      const file = new File([audioBlob], 'hazards.webm', {
        type: 'audio/webm'
      });
      const client = getOpenAIClient();
      const transcriptResponse = await client.audio.transcriptions.create({
        model: 'whisper-1',
        file,
        language: lang === 'es' ? 'es' : 'en'
      });
      const rawText = transcriptResponse.text.trim();
      if (!rawText) {
        setIsTranscribing(false);
        setVoiceError('No speech detected. Please try again.');
        return;
      }
      setVoiceTranscription(rawText);
      setHasRecorded(true);
      setIsTranscribing(false);
      setIsExtracting(true);
      // Build step list for context
      const stepsList = stepHazardData.map((s, i) => `Step ${i + 1}: ${s.description}`).join('\n');
      const completion = await client.chat.completions.create({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system',
          content: getHazardVoiceExtractionPrompt(stepsList)
        }, {
          role: 'user',
          content: `Transcription: "${rawText}"`
        }],
        temperature: 0.3,
        max_tokens: 400
      });
      const gptRaw = completion.choices[0]?.message?.content?.trim() || '';
      let jsonStr = gptRaw;
      const fenceMatch = gptRaw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (fenceMatch) jsonStr = fenceMatch[1].trim();
      const parsed = JSON.parse(jsonStr) as {
        hazards: {
          hazard: string;
          source: string;
          ecmDriver: ECMDriver;
          targetStep: number | 'general';
        }[];
      };
      if (parsed.hazards && Array.isArray(parsed.hazards)) {
        setExtractedHazards(parsed.hazards.map(h => ({
          hazard: h.hazard,
          source: h.source || 'Voice identified',
          ecmDriver: (['E', 'C', 'M'].includes(h.ecmDriver) ? h.ecmDriver : 'E') as ECMDriver,
          targetStep: h.targetStep === 'general' ? 'general' : typeof h.targetStep === 'number' ? Math.max(1, Math.min(h.targetStep, stepHazardData.length)) - 1 : 0,
          selected: true
        })));
      } else {
        throw new Error('Invalid extraction format');
      }
    } catch (err: any) {
      console.error('[Voice Hazard Extraction] Failed:', err);
      setVoiceError(`Failed to process audio: ${err?.message || String(err)}`);
    } finally {
      setIsTranscribing(false);
      setIsExtracting(false);
    }
  }, [stepHazardData]);
  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  const toggleExtracted = (index: number) => {
    setExtractedHazards(prev => prev.map((h, i) => i === index ? {
      ...h,
      selected: !h.selected
    } : h));
  };
  const handleApplyVoiceHazards = () => {
    const selected = extractedHazards.filter(h => h.selected);
    let updatedSteps = [...stepHazardData.map(s => ({
      ...s,
      hazards: [...s.hazards]
    }))];
    let updatedGeneral = [...generalHazards];
    for (const item of selected) {
      const newId = `h-voice-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setReviewedIds(prev => new Set(prev).add(newId));
      if (item.targetStep === 'general') {
        updatedGeneral.push({
          id: newId,
          hazard: item.hazard,
          source: item.source,
          ecmDriver: item.ecmDriver,
          controls: {
            eliminateSubstitute: 'Assess and determine',
            engineering: 'Apply engineering controls',
            adminTraining: 'Follow procedures',
            ppe: 'Standard PPE'
          },
          equipment: [],
          relevant: true
        });
      } else {
        const stepIdx = item.targetStep as number;
        if (stepIdx >= 0 && stepIdx < updatedSteps.length) {
          updatedSteps[stepIdx].hazards.push({
            id: newId,
            hazard: item.hazard,
            source: item.source,
            ecmDriver: item.ecmDriver,
            controls: {
              eliminateSubstitute: 'Assess and determine',
              engineering: 'Apply engineering controls',
              adminTraining: 'Follow procedures',
              ppe: 'Standard PPE'
            },
            equipment: [],
            relevant: true
          });
        }
      }
    }
    onUpdateStepHazardData(updatedSteps);
    onUpdateGeneralHazards(updatedGeneral);
    setShowVoiceModal(false);
    setTimeout(() => {
      setHasRecorded(false);
      setVoiceTranscription('');
      setExtractedHazards([]);
    }, 300);
  };
  const handleCloseVoiceModal = () => {
    if (isRecording || isTranscribing || isExtracting) return;
    setShowVoiceModal(false);
    setTimeout(() => {
      setHasRecorded(false);
      setVoiceTranscription('');
      setExtractedHazards([]);
      setVoiceError(null);
    }, 300);
  };
  // Cleanup
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);
  const selectedExtractedCount = extractedHazards.filter(h => h.selected).length;
  // ── Render ─────────────────────────────────────────────────────────────────
  const renderHazardCard = (hazard: StepHazardItem | GeneralHazardItem, isGeneral: boolean, animDelay: number) => {
    const isReviewed = reviewedIds.has(hazard.id);
    const isRelevant = hazard.relevant;
    return <motion.div key={hazard.id} initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: animDelay
    }} className={`rounded-lg p-3 flex items-start gap-3 border transition-colors ${isReviewed && isRelevant ? 'bg-emerald-50 border-emerald-200' : isReviewed && !isRelevant ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-white border-gray-200'}`} data-id="element-464">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border flex-shrink-0 mt-0.5 ${getEcmColorClasses(hazard.ecmDriver)}`} data-id="element-465">
          {hazard.ecmDriver}
        </span>

        <button onClick={() => setEditingHazard({
        id: hazard.id,
        text: hazard.hazard,
        isGeneral
      })} className={`flex-1 min-w-0 flex items-start gap-1.5 text-left group`} data-id="element-466">
          <p className={`text-sm font-semibold leading-snug ${isReviewed && isRelevant ? 'text-emerald-700' : isReviewed && !isRelevant ? 'text-gray-400 line-through' : 'text-gray-800'}`} data-id="element-467">
            {hazard.hazard}
          </p>
          <PencilIcon className="w-3 h-3 text-gray-400 group-hover:text-gray-500 flex-shrink-0 mt-0.5 transition-colors" data-id="element-468" />
        </button>

        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-469">
          <button onClick={() => handleSetRelevant(hazard.id, true, isGeneral)} className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${isReviewed && isRelevant ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30' : 'bg-gray-100 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600'}`} data-id="element-470">
            <CheckIcon className="w-4 h-4" data-id="element-471" />
          </button>
          <button onClick={() => handleSetRelevant(hazard.id, false, isGeneral)} className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${isReviewed && !isRelevant ? 'bg-gray-400 text-white' : 'bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500'}`} data-id="element-472">
            <XIcon className="w-4 h-4" data-id="element-473" />
          </button>
        </div>
      </motion.div>;
  };
  let animCounter = 0;
  return <div className="flex flex-col h-full relative" data-id="element-474">
      <div className="mb-2" data-id="element-475">
        <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-476">
          {t('hazard.title')}
        </h2>
        <p className="text-gray-500 text-xs" data-id="element-477">{t('hazard.subtitle')}</p>
      </div>

      <div className="flex items-center justify-between mb-3" data-id="element-478">
        <div className="flex items-center gap-2" data-id="element-479">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-480">
            {t('summary.hazards')}
          </p>
          <span className="text-xs text-gray-400 font-medium" data-id="element-481">
            {reviewedCount}/{totalCount} {t('hazard.reviewed')}
          </span>
          <button onClick={() => {
          setShowAddInput(true);
          setAddTargetStep(0);
        }} className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-yellow-500 hover:border-gray-300 transition-colors" data-id="element-482">
            <PlusIcon className="w-3.5 h-3.5" data-id="element-483" />
          </button>
        </div>
        <button onClick={() => setShowVoiceModal(true)} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" data-id="element-484">
          <MicIcon className="w-3.5 h-3.5 text-yellow-500" data-id="element-485" />
          {t('hazard.addHazard')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 space-y-1 -mx-2 px-2" data-id="element-486">
        {/* Manual Add Input */}
        <AnimatePresence data-id="element-487">
          {showAddInput && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="overflow-hidden" data-id="element-488">
              <div className="bg-white border border-yellow-400 rounded-lg p-3 mb-2 space-y-2" data-id="element-489">
                <div className="flex items-center gap-2" data-id="element-490">
                  <select value={addTargetStep === 'general' ? 'general' : addTargetStep} onChange={e => {
                const val = e.target.value;
                setAddTargetStep(val === 'general' ? 'general' : parseInt(val));
              }} className="bg-gray-100 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 outline-none flex-shrink-0" data-id="element-491">
                    {stepHazardData.map((step, i) => <option key={i} value={i} data-id="element-492">
                        Step {i + 1}
                      </option>)}
                    <option value="general" data-id="element-493">
                      {t('hazard.generalHazards')}
                    </option>
                  </select>
                  <input autoFocus value={newHazardText} onChange={e => setNewHazardText(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') handleAddHazard();
                if (e.key === 'Escape') {
                  setShowAddInput(false);
                  setNewHazardText('');
                }
              }} placeholder={t('hazard.describePlaceholder')} className="flex-1 bg-transparent text-gray-900 text-sm outline-none placeholder-gray-400" data-id="element-494" />
                </div>
                <div className="flex justify-end gap-2" data-id="element-495">
                  <button onClick={() => {
                setShowAddInput(false);
                setNewHazardText('');
              }} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-400" data-id="element-496">
                    <XIcon className="w-4 h-4" data-id="element-497" />
                  </button>
                  <button onClick={handleAddHazard} disabled={!newHazardText.trim()} className="w-8 h-8 rounded-lg bg-yellow-400 flex items-center justify-center flex-shrink-0 disabled:opacity-30 disabled:bg-gray-200 transition-all" data-id="element-498">
                    <CheckIcon className="w-4 h-4 text-gray-900" data-id="element-499" />
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Step Hazard Sections */}
        {stepHazardData.map((step, stepIdx) => <div key={stepIdx} className="space-y-2" data-id="element-500">
            <div className="flex items-center gap-2 pt-3 pb-1" data-id="element-501">
              <span className="w-5 h-5 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500" data-id="element-502">
                {stepIdx + 1}
              </span>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate flex-1" data-id="element-503">
                {step.description}
              </p>
            </div>
            {step.hazards.map(hazard => {
          const delay = animCounter * 0.03;
          animCounter++;
          return renderHazardCard(hazard, false, delay);
        })}
            {step.hazards.length === 0 && <p className="text-gray-400 text-xs italic pl-7 py-1" data-id="element-504">
                No hazards identified for this step.
              </p>}
          </div>)}

        {/* General Hazards Section */}
        {generalHazards.length > 0 && <div className="space-y-2" data-id="element-505">
            <div className="flex items-center gap-2 pt-4 pb-1" data-id="element-506">
              <div className="w-5 h-5 rounded bg-gray-100 border border-gray-200 flex items-center justify-center" data-id="element-507">
                <ShieldAlertIcon className="w-3 h-3 text-gray-500" data-id="element-508" />
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-509">
                {t('hazard.generalHazards')}
              </p>
            </div>
            {generalHazards.map(hazard => {
          const delay = animCounter * 0.03;
          animCounter++;
          return renderHazardCard(hazard, true, delay);
        })}
          </div>}
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-510">
        <PrimaryButton onClick={onNext} disabled={reviewedCount < totalCount || totalCount === 0} data-id="element-511">
          {t('continue')}
        </PrimaryButton>
      </div>

      {/* ── Voice Input Modal ───────────────────────────────────────────────── */}
      <AnimatePresence data-id="element-512">
        {showVoiceModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={handleCloseVoiceModal} className="absolute inset-0 bg-black/30 backdrop-blur-sm z-40" data-id="element-513" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 rounded-t-2xl z-50 px-5 pt-4 pb-6 max-h-[85%] flex flex-col overflow-hidden" data-id="element-514">
              <div className="flex items-center justify-between mb-2" data-id="element-515">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-516">
                  {t('hazard.addHazards')}
                </h3>
                <button onClick={handleCloseVoiceModal} disabled={isRecording || isTranscribing || isExtracting} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100 disabled:opacity-50" data-id="element-517">
                  <XIcon className="w-5 h-5" data-id="element-518" />
                </button>
              </div>

              <p className="text-gray-500 text-sm mb-4" data-id="element-519">
                Describe hazards you've identified. Mention the step number if
                applicable, e.g. "Add a fall hazard to step 2."
              </p>

              {/* Recording / Processing Section */}
              {extractedHazards.length === 0 && <div className="flex-1 flex flex-col items-center justify-center space-y-4 mb-4 min-h-0" data-id="element-520">
                  {isTranscribing || isExtracting ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center space-y-4" data-id="element-521">
                      <div className="w-16 h-16 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center" data-id="element-522">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1.2,
                  ease: 'linear'
                }} data-id="element-523">
                          <LoaderIcon className="w-7 h-7 text-yellow-500" data-id="element-524" />
                        </motion.div>
                      </div>
                      <div className="text-center space-y-1" data-id="element-525">
                        <p className="text-gray-900 font-medium text-sm" data-id="element-526">
                          {isTranscribing ? 'Transcribing...' : 'Extracting hazards...'}
                        </p>
                        <p className="text-gray-400 text-xs" data-id="element-527">
                          {isTranscribing ? 'Converting speech to text' : 'AI is identifying hazards and assigning to steps'}
                        </p>
                      </div>
                      {voiceTranscription && isExtracting && <div className="w-full p-3 rounded-xl bg-gray-50 border border-gray-200 mt-2 max-h-[100px] overflow-y-auto" data-id="element-528">
                          <p className="text-gray-500 font-mono text-xs leading-snug" data-id="element-529">
                            "{voiceTranscription}"
                          </p>
                        </div>}
                    </motion.div> : <>
                      <div className="relative flex items-center justify-center" data-id="element-530">
                        {isRecording && <motion.div animate={{
                  scale: [1, 1.3, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 1.5
                }} className="absolute w-24 h-24 bg-yellow-100 rounded-full" data-id="element-531" />}
                        {isRecording && <motion.div animate={{
                  scale: [1, 1.15, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  delay: 0.2
                }} className="absolute w-20 h-20 bg-yellow-200 rounded-full" data-id="element-532" />}
                        <motion.button whileTap={{
                  scale: 0.9
                }} onClick={handleRecordToggle} className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition-colors ${isRecording ? 'bg-red-500 shadow-red-400/30' : 'bg-yellow-400 shadow-yellow-400/30'}`} data-id="element-533">
                          {isRecording ? <SquareIcon className="w-6 h-6 text-white fill-current" data-id="element-534" /> : <MicIcon className="w-6 h-6 text-gray-900" data-id="element-535" />}
                        </motion.button>
                      </div>

                      <div className="text-center h-6" data-id="element-536">
                        <p className={`font-medium text-sm ${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-500'}`} data-id="element-537">
                          {isRecording ? 'Recording...' : hasRecorded ? 'Tap to re-record' : 'Tap to start'}
                        </p>
                      </div>

                      {voiceError && <p className="text-red-500 text-xs text-center max-w-[250px] mt-2" data-id="element-538">
                          {voiceError}
                        </p>}
                    </>}
                </div>}

              {/* Extracted Hazards List */}
              {extractedHazards.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex-1 flex flex-col min-h-0 mb-4" data-id="element-539">
                  <div className="flex items-center gap-2 mb-3" data-id="element-540">
                    <SparklesIcon className="w-4 h-4 text-yellow-500" data-id="element-541" />
                    <p className="text-sm font-semibold text-gray-900" data-id="element-542">
                      AI found {extractedHazards.length} hazard
                      {extractedHazards.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div className="space-y-2 overflow-y-auto flex-1 min-h-0 pr-1" data-id="element-543">
                    {extractedHazards.map((item, index) => <motion.button key={index} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} onClick={() => toggleExtracted(index)} className={`w-full rounded-lg p-3 flex items-start gap-3 border text-left transition-all ${item.selected ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-100 border-gray-200 opacity-60'}`} data-id="element-544">
                        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${item.selected ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'}`} data-id="element-545">
                          {item.selected && <CheckIcon className="w-3.5 h-3.5" data-id="element-546" />}
                        </div>

                        <div className="flex-1" data-id="element-547">
                          <div className="flex items-center gap-2 mb-1" data-id="element-548">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border flex-shrink-0 ${getEcmColorClasses(item.ecmDriver)}`} data-id="element-549">
                              {item.ecmDriver}
                            </span>
                            <span className="text-[10px] text-gray-400 font-medium" data-id="element-550">
                              →{' '}
                              {item.targetStep === 'general' ? 'General' : `Step ${(item.targetStep as number) + 1}`}
                            </span>
                          </div>
                          <p className={`text-sm font-medium leading-snug ${item.selected ? 'text-emerald-700' : 'text-gray-500'}`} data-id="element-551">
                            {item.hazard}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5" data-id="element-552">
                            {item.source}
                          </p>
                        </div>
                      </motion.button>)}
                  </div>

                  <button onClick={() => {
              setExtractedHazards([]);
              setHasRecorded(false);
              setVoiceTranscription('');
            }} className="mt-3 text-xs text-gray-400 hover:text-gray-700 transition-colors text-center" data-id="element-553">
                    Re-record instead
                  </button>
                </motion.div>}

              {/* Action Button */}
              {extractedHazards.length > 0 && <PrimaryButton onClick={handleApplyVoiceHazards} disabled={selectedExtractedCount === 0} data-id="element-554">
                  Add {selectedExtractedCount} Hazard
                  {selectedExtractedCount !== 1 ? 's' : ''}
                </PrimaryButton>}
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Edit Hazard Modal */}
      <EditItemModal isOpen={!!editingHazard} initialText={editingHazard?.text || ''} title="Edit Hazard" onSave={newText => {
      if (editingHazard) handleEditHazard(editingHazard.id, newText, editingHazard.isGeneral);
    }} onCancel={() => setEditingHazard(null)} data-id="element-555" />
    </div>;
}